import { Alert, Box, Container, Typography, useTheme } from '@mui/material';
import axios from "axios";
import React, { useEffect, useState } from 'react';
import { Navigate } from "react-router-dom";
import LoadingSpinnerCentred from "../Components/LoadingSpinnerCentred";

export default function UserProgress({ user, darkMode })
{
    const theme = useTheme();
    const [metrics, setMetrics] = useState(null);
    const [busy, setBusy] = useState(true);
    const [alertInfo, setAlertInfo] = useState(null);
    const [animatedProgress, setAnimatedProgress] = useState({});

    useEffect(() =>
    {
        axios.get('api/users/question-metrics', { withCredentials: true }).then(result =>
        {
            setMetrics(result.data);
            setBusy(false);

            const duration = 1000;
            const frames = 60;
            const interval = duration / frames;
            let step = 0;

            const easeOut = t => 1 - Math.pow(1 - t, 3);

            const animate = () =>
            {
                const timeElapst = easeOut(step / frames);

                setAnimatedProgress(() =>
                {
                    const progress = {};

                    const animateEntry = (label, total, answered, correct) =>
                    {
                        return { label, total, answered: Math.round(answered * timeElapst), correct: Math.round(correct * timeElapst) };
                    };

                    progress["Overall"] = animateEntry("Overall", result.data.total, result.data.answered, result.data.answeredCorrectly);
                    result.data.categories.forEach(c =>
                    {
                        progress[c.category] = animateEntry(c.category, c.total, c.answered, c.answeredCorrectly);
                    });

                    return progress;
                });

                step++;
                if (step <= frames)
                    setTimeout(animate, interval);
            };

            animate();
        }).catch(() =>
        {
            setAlertInfo({ type: "error", message: "Failed to load metrics" });
            setBusy(false);
        });
    }, []);

    if (busy)
        return <LoadingSpinnerCentred topMargin={true} />;

    if (user === null)
        return <Navigate to="/" />;

    const getRed = () => darkMode ? '#F39786' : '#D03A2F';
    const getGray = () => darkMode ? '#999' : '#ccc';
    const getTextColor = () => darkMode ? 'black' : 'white';
    const showLabel = (percent, value, label) => percent > 10 ? `${value} ${label}` : percent > 1 ? `${value}` : null;

    function renderBar(label, progress)
    {
        const { total, answered, correct } = progress;
        const unanwered = total - answered;
        const incorrect = answered - correct;
        const correctPercent = total ? (correct / total) * 100 : 0;
        const incorrectPercent = total ? (incorrect / total) * 100 : 0;
        const unanweredPercent = total ? (unanwered / total) * 100 : 0;

        return (
            <Box key={label} sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{label}</Typography>
                <Box sx={{
                    display: 'flex',
                    height: 30,
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: getGray(),
                }}>
                    <Box sx={{
                        width: `${correctPercent}%`,
                        backgroundColor: theme.palette.primary.main,
                        color: getTextColor(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        {correct > 0 && (
                            <Typography variant="body2" noWrap>
                                {showLabel(correctPercent, correct, `correct`)}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{
                        width: `${incorrectPercent}%`,
                        backgroundColor: getRed(),
                        color: getTextColor(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        {incorrect > 0 && (
                            <Typography variant="body2" noWrap>
                                {showLabel(incorrectPercent, incorrect, `incorrect`)}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{
                        width: `${unanweredPercent}%`,
                        backgroundColor: getGray(),
                        color: getTextColor(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        {unanwered > 0 && (
                            <Typography variant="body2" noWrap>
                                {showLabel(unanweredPercent, unanwered, `unanswered`)}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{total} questions total</Typography>
            </Box>
        );
    }

    return (
        <Container component="main" maxWidth="md" sx={{ mt: 8 }}>
            <Typography component="h2" variant="h5" sx={{ mb: 2 }}>Your Progress</Typography>

            {alertInfo && <Alert severity={alertInfo.type}>{alertInfo.message}</Alert>}

            {animatedProgress["Overall"] && renderBar("Overall", animatedProgress["Overall"])}

            {metrics.categories.map(c =>
                animatedProgress[c.category] && renderBar(c.category, animatedProgress[c.category])
            )}
        </Container>
    );
}
